import { CircularProgress } from '@mui/material';
import React, { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import toast, { Toaster } from 'react-hot-toast';
import { api } from '../../../services';

// Types
// (Same as before)

type MediaInsight = {
    mediaId: string;
    mediaLen: number;
    frequency: number;
    actualPlay: number;
    playTime?: string[] | null;
    skipReason?: string | null;
};

type CampaignInsight = {
    campaignId: string;
    campaignLength: string;
    completed: boolean;
    mediaInsights: MediaInsight[];
};

type ScreenCampaignInfo = {
    screenId: string;
    slot: number;
    screenQuota: number;
    campaignDurationInMin: number;
    merchantCampaignDurationInMin: number;
    merchantCampaignInsights: CampaignInsight[];
    advertiserCampaignDurationInMin: number;
    advertiserCampaignInsights: CampaignInsight[];
};

type ScreenInsightResponse = {
    screenInsights: ScreenCampaignInfo[];
    totalPages: number | null;

};

type FullApiResponse = {

    response: ScreenInsightResponse;

    errorMessage: string | null;
    errorCode: string | null;
};

const ScreenCampaignInsights: React.FC = () => {
    const [screenId, setScreenId] = useState('');
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [data, setData] = useState<ScreenCampaignInfo[]>([]);
    const [expandedSlot, setExpandedSlot] = useState<number | null>(null);
    const [expandedCampaign, setExpandedCampaign] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [totalPages, setTotalPages] = useState<number | null>(null);
    const [currentPage, setCurrentPage] = useState(0);

    const handleToggleSlot = (slot: number) => {
        setExpandedSlot(prev => (prev === slot ? null : slot));
        setExpandedCampaign(null);
    };

    const handleToggleCampaign = (campaignId: string) => {
        setExpandedCampaign(prev => (prev === campaignId ? null : campaignId));
    };



    const handleFetch = async (page: number = 0) => {
        if (!screenId || !startDate || !endDate) 
        {
            toast.error("Please Fill all the required fields")
            return;
        };

        const startEpoch = Math.floor(startDate.getTime() / 1000);
        const endEpoch = Math.floor(endDate.getTime() / 1000);

        try {
            setLoading(true);
            setError('');
            const res = await api.get<FullApiResponse>(
                `/admin/campaign/insight/screenId/${screenId}/startSlot/${startEpoch}/endSlot/${endEpoch}/page/${page}`
            );
            setData(res.data?.response?.screenInsights ?? []);
            setTotalPages(res.data?.response?.totalPages ?? null);
            setCurrentPage(page);
        } catch (err) {
            console.error(err);
            setError('Failed to fetch insights.');
        } finally {
            setLoading(false);
        }
    };
    useEffect(()=>{
        console.log("length",data.length); 
    },[data])


    return <>
    <Toaster/>
        <div className="p-6">
            <h2 className="text-2xl font-bold mb-4">Screen Campaign Insights</h2>

            <div className="flex flex-wrap gap-10 mb-6 items-center">
                <input
                    type="text"
                    placeholder="Screen ID"
                    className="border p-2 rounded outline-none"
                    value={screenId}
                    onChange={e => setScreenId(e.target.value)}
                />
                <DatePicker
                    selected={startDate}
                    onChange={(date) => setStartDate(date)}
                    showTimeSelect
                    dateFormat="yyyy-MM-dd HH:mm"
                    placeholderText="Start Date & Time"
                    className="border p-2 rounded outline-none "
                />
                <DatePicker
                    selected={endDate}
                    onChange={(date) => setEndDate(date)}
                    showTimeSelect
                    dateFormat="yyyy-MM-dd HH:mm"
                    placeholderText="End Date & Time"
                    className="border p-2 rounded outline-none "
                />
                <button
                    onClick={()=>handleFetch(0)}
                    className="bg-blue-600 text-white px-4 py-2 rounded"
                >
                    Fetch
                </button>
            </div>

            {error && <div className="text-red-600 mb-4">{error}</div>}
            {loading && <div className="text-gray-500  flex justify-center items-center h-[50vh]"><CircularProgress /></div>}

            {!loading && data.length > 0 && (
                <div className="border rounded shadow">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-gray-100">
                                <th className="border p-2">Screen ID</th>
                                <th className="border p-2">Slot</th>
                                <th className="border p-2">Quota</th>
                                <th className="border p-2">Total Duration</th>
                                <th className="border p-2">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.map(info => (
                                <React.Fragment key={info.slot}>
                                    <tr className="hover:bg-gray-50">
                                        <td className="border p-2">{info.screenId}</td>
                                        <td className="border p-2">{info.slot}</td>
                                        <td className="border p-2">{info.screenQuota}</td>
                                        <td className="border p-2">{info.campaignDurationInMin} min</td>
                                        <td className="border p-2">
                                            <button
                                                onClick={() => handleToggleSlot(info.slot)}
                                                className="text-blue-600 underline"
                                            >
                                                {expandedSlot === info.slot ? 'Collapse' : 'Expand'}
                                            </button>
                                        </td>
                                    </tr>

                                    {expandedSlot === info.slot &&
                                        [...(info.merchantCampaignInsights ?? []), ...(info.advertiserCampaignInsights ?? [])].map(camp => (
                                            <React.Fragment key={camp.campaignId}>
                                                <tr className="bg-gray-50">
                                                    <td colSpan={5} className="border p-2 pl-6">
                                                        <div className="flex justify-between items-center">
                                                            <div>
                                                                <span className="font-medium">Campaign ID:</span> {camp.campaignId}
                                                                <span className="ml-4 font-medium">Length:</span> {camp.campaignLength} min
                                                                <span className="ml-4 font-medium">Completed:</span> {camp.completed ? 'Yes' : 'No'}
                                                            </div>
                                                            <button
                                                                onClick={() => handleToggleCampaign(camp.campaignId)}
                                                                className="text-blue-500 underline"
                                                            >
                                                                {expandedCampaign === camp.campaignId ? 'Hide Media' : 'Show Media'}
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>

                                                {expandedCampaign === camp.campaignId &&
                                                    (camp.mediaInsights ?? []).map(media => (
                                                        <tr key={media.mediaId} className="bg-gray-100">
                                                            <td colSpan={5} className="border p-2 pl-10">
                                                                <div className="grid grid-cols-2 gap-y-1">
                                                                    <div><strong>Media ID:</strong> {media.mediaId}</div>
                                                                    <div><strong>Length:</strong> {media.mediaLen} sec</div>
                                                                    <div><strong>Frequency:</strong> {media.frequency}</div>
                                                                    <div><strong>Actual Play:</strong> {media.actualPlay}</div>
                                                                    {media.skipReason && (
                                                                        <div><strong>Skip Reason:</strong> {media.skipReason}</div>
                                                                    )}
                                                                    {Array.isArray(media.playTime) && media.playTime.length > 0 && (
                                                                        <div className="col-span-2">
                                                                            <strong>Play Times:</strong>
                                                                            <ul className="list-disc list-inside text-sm">
                                                                                {media.playTime.map((pt, idx) => (
                                                                                    <li key={idx}>{pt}</li>
                                                                                ))}
                                                                            </ul>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                            </React.Fragment>
                                        ))}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                    {totalPages !== null && totalPages > 1 && (
                        <div className="flex justify-center items-center gap-4 mt-4">
                            <button
                                onClick={() => handleFetch(currentPage-1)}
                                disabled={currentPage === 0}
                                className="bg-gray-300 px-3 py-1 rounded disabled:opacity-50"
                            >
                                Previous
                            </button>
                            <span className="font-medium">Page {currentPage + 1} of {totalPages}</span>
                            <button
                                onClick={() => handleFetch(currentPage+1)} 
                                disabled={currentPage + 1 >= totalPages}
                                className="bg-gray-300 px-3 py-1 rounded disabled:opacity-50"
                            >
                                Next
                            </button>
                        </div>
                    )}


                </div>
            )}
        </div>
        </>;
};

export default ScreenCampaignInsights;
